import type { ReactNode } from 'react';
import { cn } from '@/shared/lib/cn';
import { Icon } from '@/shared/ui/icon';
import { useT } from '@/shared/i18n';
import type { SupportedLocale } from '@/app/config/app.config';

/**
 * App-shell locale toggle — Languages icon + the short ISO label of the locale
 * the user will switch to (EN / AR), matching the theme toggle's target-state
 * convention. The accessible name stays direction-explicit so screen readers
 * announce the action. Sized to sit beside the 40px theme toggle in the top bar.
 *
 * Pass `onInk` when rendering on the dark Ink band (the mobile drawer footer)
 * so the icon + label stay legible against the dark surface.
 */
export function AppLocaleToggle({ onInk = false }: { onInk?: boolean }): ReactNode {
  const { t, locale, setLocale } = useT();
  const otherLocale: SupportedLocale = locale === 'en' ? 'ar' : 'en';
  const shortLabel = otherLocale === 'en' ? t('locale.short.en') : t('locale.short.ar');

  return (
    <button
      type="button"
      onClick={() => setLocale(otherLocale)}
      aria-label={t('locale.toggle.aria.label')}
      data-testid="app-locale-toggle"
      className={cn(
        'inline-flex h-10 items-center gap-1.5 rounded-md px-2.5 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary-fixed/60',
        onInk
          ? 'text-[var(--on-ink-band)] hover:bg-[var(--surface-overlay)]'
          : 'text-[var(--text-primary)] hover:bg-[var(--surface-container)]',
      )}
    >
      <Icon name="languages" size={18} />
      <span aria-hidden="true">{shortLabel}</span>
    </button>
  );
}
